/**
 * LSC - lsc-mobile-menu.js (added 2026-10-18)
 * ------------------------------------------------------------------------
 * Purpose: make the mobile hamburger menu's JavaScript-driven animations run
 * at twice their normal speed (every duration, delay and timer halved).
 *
 * Companion file: assets/lsc-mobile-menu.css recolours the drawer (navy
 * #355266 / white) and halves the CSS transitions. Between the two files,
 * every moving part of the drawer is 2x faster.
 *
 * What theme.js does today, and what this file changes it to:
 *
 *   <menu-drawer> (class MenuDrawer, extends DrawerElement -> ModalElement)
 *     showTransition()  waits  75ms before adding [active]       -> 38ms
 *                       ([active] is what starts the CSS slide/fade)
 *     beforeShow()      waits 300ms, then slides + fades each top-level
 *                       item in over 0.6s, 0.1s apart            -> 150ms,
 *                                                                   0.3s,
 *                                                                   0.05s apart
 *     beforeHide()      waits 300ms, then closes any open submenu -> 150ms
 *                       (so the drawer reopens at the top level)
 *
 *   <details is="menu-details"> (class MenuDetails, the submenus)
 *     onSummaryClick()  waits 100ms before sliding the submenu in,
 *                       then 100ms more before moving focus into it -> 50ms + 50ms
 *     closeAnimation()  waits 400ms for the slide-out before
 *                       closing the <details>                     -> 200ms
 *
 * Why patch prototypes instead of editing assets/theme.js:
 *   theme.js is a 280KB minified bundle that a theme update would overwrite.
 *   Patching from our own file keeps the change in one readable place that
 *   survives updates and can be removed in one step. The replacement methods
 *   below are the theme's own logic with only the numbers changed, so the
 *   behaviour (accessibility, focus trapping, cleanup) is unchanged.
 *
 * Scope: only the mobile menu is affected.
 *   - MenuDrawer is used only by #MenuDrawer.
 *   - MenuDetails patches only apply inside #MenuDrawer; any other
 *     menu-details element on the site falls through to the original code.
 *   - ModalElement / DrawerElement themselves are NOT modified, so the cart
 *     drawer, search drawer, quick view and other modals keep their timing.
 *
 * Swipe-to-close: the finger-tracking transitions set during a swipe
 * (0.1s linear) follow the finger rather than playing an animation, so they
 * are left as they are.
 *
 * Revert: remove the <script> tag for this file in layout/theme.liquid.
 */
(function () {
  'use strict';

  /* Every timing in this file is the theme's original value multiplied by
     this factor. 0.5 = twice as fast. Change it here to retune everything
     at once (it should match the halving done in lsc-mobile-menu.css). */
  var SPEED = 0.5;

  /* Original theme values, kept as named constants so each halved number
     can be traced back to what theme.js uses. */
  var ORIGINAL = {
    activeDelayMs: 75,       // ModalElement.showTransition -> [active]
    itemsDelayMs: 300,       // MenuDrawer.beforeShow wait
    itemsDurationS: 0.6,     // MenuDrawer.beforeShow item slide/fade
    itemsStaggerS: 0.1,      // MenuDrawer.beforeShow gap between items
    resetSubmenusMs: 300,    // MenuDrawer.beforeHide wait
    submenuOpenMs: 100,      // MenuDetails.onSummaryClick first wait
    submenuFocusMs: 100,     // MenuDetails.onSummaryClick second wait
    submenuCloseMs: 400      // MenuDetails.closeAnimation wait
  };

  /** Scale a theme timing by SPEED (rounded for millisecond timers). */
  function fastMs(ms) { return Math.round(ms * SPEED); }
  function fastS(s) { return s * SPEED; }

  /* Selector for the mobile menu. Used to keep the MenuDetails patch from
     touching any other menu-details element. */
  var DRAWER_SELECTOR = '#MenuDrawer';


  /* ----------------------------------------------------------------------
     <menu-drawer>: opening and closing the whole drawer
     ---------------------------------------------------------------------- */
  function patchMenuDrawer() {
    var MenuDrawer = customElements.get('menu-drawer');
    var DrawerElement = customElements.get('drawer-element');

    // Bail out safely if the theme ever renames these elements, or if this
    // script somehow runs twice.
    if (!MenuDrawer || !DrawerElement || MenuDrawer.prototype.__lscFastMenu) return;

    var proto = MenuDrawer.prototype;
    var parentProto = DrawerElement.prototype;

    /**
     * Same as ModalElement.showTransition, but adds [active] after 38ms
     * instead of 75ms. [active] is the attribute the CSS keys the sheet
     * slide, backdrop fade and content fade off.
     *
     * The returned promise resolves when the backdrop's fade finishes (or
     * immediately if it has no transition), exactly as the theme's does;
     * ModalElement waits on it before firing afterShow / trapping focus.
     */
    proto.showTransition = function () {
      var self = this;

      setTimeout(function () {
        self.setAttribute('active', '');
      }, fastMs(ORIGINAL.activeDelayMs));

      return new Promise(function (resolve) {
        var overlay = self.overlay;
        if (!overlay) { resolve(); return; }

        var computed = window.getComputedStyle(overlay);
        var animates = computed.transitionProperty !== 'none' &&
          parseFloat(computed.transitionDuration) > 0;

        if (!animates) { resolve(); return; }
        overlay.addEventListener('transitionend', resolve, { once: true });
      });
    };

    /**
     * Same as MenuDrawer.beforeShow, at double speed: once the sheet is part
     * way in, slide each top-level menu item in from the left and fade it
     * up, one after another. Inline styles are removed when done so CSS is
     * back in control (as in the theme).
     */
    proto.beforeShow = function () {
      parentProto.beforeShow.call(this);

      var self = this;
      setTimeout(function () {
        // Motion is the animation library bundled in vendor.js (global).
        if (!window.Motion || typeof Motion.animate !== 'function') return;

        Motion.animate(
          self.menuItems,
          {
            transform: ['translateX(-20px)', 'translateX(0)'],
            opacity: [0, 1]
          },
          {
            duration: fastS(ORIGINAL.itemsDurationS),
            easing: [0.075, 0.82, 0.165, 1],          // theme's own curve
            delay: Motion.stagger(fastS(ORIGINAL.itemsStaggerS))
          }
        ).finished.then(function () {
          self.menuItems.forEach(function (item) {
            item.removeAttribute('style');
          });
        });
      }, fastMs(ORIGINAL.itemsDelayMs));
    };

    /**
     * Same as MenuDrawer.beforeHide, at double speed: after the drawer starts
     * closing, close any submenu that was left open so the menu reopens at
     * the top level next time.
     */
    proto.beforeHide = function () {
      parentProto.beforeHide.call(this);

      var self = this;
      setTimeout(function () {
        self.querySelectorAll('details[is=menu-details]').forEach(function (subMenu) {
          if (typeof subMenu.onCloseButtonClick === 'function') {
            subMenu.onCloseButtonClick();
          }
        });
      }, fastMs(ORIGINAL.resetSubmenusMs));
    };

    // Marker so the patch is never applied twice.
    proto.__lscFastMenu = true;
  }


  /* ----------------------------------------------------------------------
     <details is="menu-details">: opening and closing a submenu
     ---------------------------------------------------------------------- */
  function patchMenuDetails() {
    var MenuDetails = customElements.get('menu-details');
    if (!MenuDetails || MenuDetails.prototype.__lscFastMenu) return;

    var proto = MenuDetails.prototype;
    var originalSummaryClick = proto.onSummaryClick;
    var originalCloseAnimation = proto.closeAnimation;

    if (typeof originalSummaryClick !== 'function' ||
        typeof originalCloseAnimation !== 'function') return;

    /** True when this submenu belongs to the mobile menu drawer. */
    function inMobileMenu(el) {
      return !!(el && el.closest && el.closest(DRAWER_SELECTOR));
    }

    /**
     * Same as MenuDetails.onSummaryClick, at double speed: open the
     * <details>, slide the submenu panel in, then move keyboard focus into
     * it (to its "<" back button) and trap it there.
     *
     * Note: theme.js binds this handler with .bind(this) when the element
     * connects, so submenus already on the page captured the ORIGINAL
     * method. rebindExisting() below fixes those; submenus that arrive later
     * (nested levels loaded through the Section Rendering API) connect after
     * this patch and pick this version up on their own.
     */
    proto.onSummaryClick = function (event) {
      if (!inMobileMenu(this)) {
        return originalSummaryClick.call(this, event);
      }

      event.preventDefault();
      this.setAttribute('open', '');
      this.activeElement = event.currentTarget;

      var self = this;
      setTimeout(function () {
        self.parent.classList.add('active');
        self.classList.add('active');
        self.summary.setAttribute('aria-expanded', true);
        self.summary.setAttribute('tabindex', '-1');

        setTimeout(function () {
          theme.a11y.trapFocus(self, self.focusElement);
        }, fastMs(ORIGINAL.submenuFocusMs));
      }, fastMs(ORIGINAL.submenuOpenMs));
    };

    /**
     * Same as MenuDetails.closeAnimation, at double speed: wait for the
     * submenu's slide-out (now 0.25s in CSS) to finish, then close the
     * <details> and hand focus back to the item that opened it.
     */
    proto.closeAnimation = function () {
      if (!inMobileMenu(this)) {
        return originalCloseAnimation.call(this);
      }

      var self = this;
      var waitMs = fastMs(ORIGINAL.submenuCloseMs);
      var start;

      function step(time) {
        if (start === undefined) start = time;

        if (time - start < waitMs) {
          requestAnimationFrame(step);
        } else {
          self.removeAttribute('open');
          theme.a11y.removeTrapFocus(self.activeElement);
        }
      }

      requestAnimationFrame(step);
    };

    proto.__lscFastMenu = true;

    rebindExisting();
  }

  /**
   * Re-attach the click listeners of submenus that were already connected
   * before the patch, so they use the fast onSummaryClick above.
   *
   * The theme's BaseElementMixin registers every listener with one
   * AbortController per element: disconnectedCallback() aborts them all and
   * connectedCallback() adds them again (bound to whatever the prototype
   * holds now). Calling the two lifecycle methods directly swaps the
   * handlers WITHOUT moving the element in the DOM, so nothing is
   * re-rendered or re-fetched. MenuDetails registers no other cleanups, so
   * this has no other side effects.
   */
  function rebindExisting() {
    var drawer = document.querySelector(DRAWER_SELECTOR);
    if (!drawer) return;

    drawer.querySelectorAll('details[is=menu-details]').forEach(function (el) {
      if (typeof el.disconnectedCallback !== 'function' ||
          typeof el.connectedCallback !== 'function') return;
      el.disconnectedCallback();
      el.connectedCallback();
    });
  }


  /* ----------------------------------------------------------------------
     Apply the patches once theme.js has defined the elements. This file is
     loaded (deferred) after theme.js, but waiting on the definitions makes
     it safe regardless of load order. Patching the prototypes also covers
     elements that are already on the page.
     ---------------------------------------------------------------------- */
  if (window.customElements && customElements.whenDefined) {
    Promise.all([
      customElements.whenDefined('drawer-element'),
      customElements.whenDefined('menu-drawer')
    ]).then(patchMenuDrawer);

    customElements.whenDefined('menu-details').then(patchMenuDetails);
  }
})();
